import { Component, Input, Output, EventEmitter, SimpleChanges, Inject, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppPermissionService } from '../../services/app-permission.service';
import { FileUtilsService } from '../../services/file-utils.service';

interface PreviewFile {
    name: string;
    url: string;
}

@Component({
    selector: 'app-file-preview-popup',
    templateUrl: './file-preview-popup.component.html',
    styleUrls: ['./file-preview-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FilePreviewPopupComponent {
    @Input() files: PreviewFile[] = []; // Array of files to preview
    @Input() startIndex: number = 0; // Index of the initially displayed file
    @Output() closed = new EventEmitter<void>();
    @Input() isMobileApp: boolean = false;
    @Input() isCCU: boolean = false;

    isLoading: boolean = false;
    currentIndex = 0;
    prevBodyOverflow = '';
    prevHtmlOverflow = '';
    textPreviewContent = '';
    textPreviewError = '';
    textPreviewLoading = false;
    private cachedPdfSourceUrl = '';
    private cachedTrustedPdfUrl: SafeResourceUrl | null = null;
    private cachedOfficeSourceUrl = '';
    private cachedTrustedOfficeViewerUrl: SafeResourceUrl | null = null;
    
    constructor(
        private appPermissionService: AppPermissionService,
        private sanitizer: DomSanitizer,
        private fileUtilsService: FileUtilsService,
        @Inject(DOCUMENT) private document: Document
    ) {}

    ngOnInit(): void {
        const body = this.document.body;
        const html = this.document.documentElement;

        this.prevBodyOverflow = body.style.overflow;
        this.prevHtmlOverflow = html.style.overflow;

        // Disable scroll on main document when popup is opened
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';

        this.loadTextPreviewIfNeeded();
    }

    ngOnDestroy(): void {
        this.restoreScroll();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['startIndex'] || changes['files']) {
            this.currentIndex = typeof this.startIndex === 'number' ? this.startIndex : 0;
        }

        if (changes['startIndex'] || changes['files']) {
            this.loadTextPreviewIfNeeded();
        }
    }

    get hasMultiple(): boolean {
        return this.files && this.files.length > 1;
    }

    get currentFile(): PreviewFile | null {
        return this.files && this.files.length
        ? this.files[this.currentIndex] || null
        : null;
    }

    get currentUrl(): string | null {
        return this.currentFile ? this.currentFile.url : null;
    }

    get currentFileExtension(): string {
        const fileName = this.currentFileName || '';
        const fileUrl = this.currentUrl || '';
        const source = fileName.includes('.') ? fileName : fileUrl;
        const sanitizedSource = source.split('?')[0].split('#')[0];
        const dotIndex = sanitizedSource.lastIndexOf('.');

        if (dotIndex === -1) {
            return '';
        }

        return sanitizedSource.substring(dotIndex + 1).toLowerCase();
    }

    get isImageFile(): boolean {
        const extension = this.currentFileExtension;
        return ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(extension);
    }

    get isPdfFile(): boolean {
        return this.currentFileExtension === 'pdf' || this.currentMimeType === 'application/pdf';
    }

    get isVideoFile(): boolean {
        const extension = this.currentFileExtension;
        return ['mp4', 'mov', 'mkv', 'avi', 'webm', 'm4v'].includes(extension)
            || this.currentMimeType.startsWith('video/');
    }

    get isAudioFile(): boolean {
        const extension = this.currentFileExtension;
        return ['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(extension)
            || this.currentMimeType.startsWith('audio/');
    }

    get isTextPreviewFile(): boolean {
        const extension = this.currentFileExtension;
        const textExtensions = ['txt', 'xml', 'json', 'log'];

        return textExtensions.includes(extension)
            || this.currentMimeType === 'text/plain'
            || this.currentMimeType === 'text/xml'
            || this.currentMimeType === 'application/json'
            || this.currentMimeType === 'application/xml';
    }

    get isOfficeDocumentFile(): boolean {
        const extension = this.currentFileExtension;
        return ['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm', 'odt'].includes(extension);
    }

    get canUseOfficeViewer(): boolean {
        return this.isOfficeDocumentFile && this.isPublicHttpUrl;
    }

    get isPublicHttpUrl(): boolean {
        const url = this.currentUrl || '';
        return /^https?:\/\//i.test(url);
    }

    get trustedPdfUrl(): SafeResourceUrl | null {
        const url = this.currentUrl;
        if (!url) {
            this.cachedPdfSourceUrl = '';
            this.cachedTrustedPdfUrl = null;
            return null;
        }

        // In Cordova WebView, direct PDF iframes often fail to render.
        // For public links in mobile app mode, use Google Docs PDF viewer embed.
        const pdfUrl = this.isMobileApp && this.isPublicHttpUrl
            ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`
            : `${url}${url.includes('#') ? '&' : '#'}view=FitH&zoom=page-fit`;
        if (this.cachedPdfSourceUrl === pdfUrl && this.cachedTrustedPdfUrl) {
            return this.cachedTrustedPdfUrl;
        }

        this.cachedPdfSourceUrl = pdfUrl;
        this.cachedTrustedPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);

        return this.cachedTrustedPdfUrl;
    }

    get trustedOfficeViewerUrl(): SafeResourceUrl | null {
        const url = this.currentUrl;
        if (!url || !this.canUseOfficeViewer) {
            this.cachedOfficeSourceUrl = '';
            this.cachedTrustedOfficeViewerUrl = null;
            return null;
        }

        // Use Office embed with fit hints so the document starts fitted to the viewport.
        const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}&wdStartOn=1&wdFitToPage=True&wdFitToWindow=True`;
        if (this.cachedOfficeSourceUrl === viewerUrl && this.cachedTrustedOfficeViewerUrl) {
            return this.cachedTrustedOfficeViewerUrl;
        }

        this.cachedOfficeSourceUrl = viewerUrl;
        this.cachedTrustedOfficeViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);

        return this.cachedTrustedOfficeViewerUrl;
    }

    // Returns the original file name for display/download UI.
    get currentFileName(): string {
        return this.currentFile ? this.currentFile.name : '';
    }

    // file type label (e.g., 'PNG', 'PDF').
    get currentFileTypeLabel(): string {
        const extension = this.currentFileExtension;

        if (!extension) {
            return 'File';
        }

        return extension.toUpperCase();
    }

    // Resolve the mime type for the current file: prefer known extension
    // mapping from the service, fall back to URL-based heuristics, and
    // default to 'application/octet-stream' if unknown.
    get currentMimeType(): string {
        const currentUrl = this.currentUrl || '';
        const extension = this.currentFileExtension;

        const mimeTypes = this.fileUtilsService.getMimeTypeForExtension(extension);

        return mimeTypes || this.getMimeTypeFromUrl(currentUrl) || 'application/octet-stream';
    }

    previousMedia(event: MouseEvent): void {
        event.stopPropagation();
        if (this.currentIndex > 0) {
            this.currentIndex--;
            this.loadTextPreviewIfNeeded();
        }
    }

    nextMedia(event: MouseEvent): void {
        event.stopPropagation();
        if (this.currentIndex < this.files.length - 1) {
            this.currentIndex++;
            this.loadTextPreviewIfNeeded();
        }
    }

    private loadTextPreviewIfNeeded(): void {
        this.textPreviewError = '';
        this.textPreviewContent = '';
        this.textPreviewLoading = false;

        if (!this.currentUrl || !this.isTextPreviewFile) {
            return;
        }

        this.textPreviewLoading = true;

        fetch(this.currentUrl)
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Unable to load file preview');
                }

                return res.text();
            })
            .then((content) => {
                this.textPreviewContent = content || '';
                this.textPreviewLoading = false;
            })
            .catch(() => {
                this.textPreviewError = 'Unable to load preview for this text file.';
                this.textPreviewLoading = false;
            });
    }

    // Fallback mime-type detection based on the URL string when the
    // extension-based lookup in the service doesn't return a value.
    // Checks for common file extensions in the URL and returns the
    // corresponding mime type, or empty string if unknown.
    private getMimeTypeFromUrl(url: string): string {
        const lowerCaseUrl = (url || '').toLowerCase();

        if (lowerCaseUrl.includes('.pdf')) return 'application/pdf';
        if (lowerCaseUrl.includes('.docm')) return 'application/vnd.ms-word.document.macroEnabled.12';
        if (lowerCaseUrl.includes('.dotm')) return 'application/vnd.ms-word.template.macroEnabled.12';
        if (lowerCaseUrl.includes('.dotx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.template';
        if (lowerCaseUrl.includes('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        if (lowerCaseUrl.includes('.dot')) return 'application/msword';
        if (lowerCaseUrl.includes('.doc')) return 'application/msword';
        if (lowerCaseUrl.includes('.odt')) return 'application/vnd.oasis.opendocument.text';
        if (lowerCaseUrl.includes('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        if (lowerCaseUrl.includes('.xls')) return 'application/vnd.ms-excel';
        if (lowerCaseUrl.includes('.pptx')) return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        if (lowerCaseUrl.includes('.ppt')) return 'application/vnd.ms-powerpoint';
        if (lowerCaseUrl.includes('.csv')) return 'text/csv';
        if (lowerCaseUrl.includes('.txt')) return 'text/plain';
        if (lowerCaseUrl.includes('.xml')) return 'application/xml';
        if (lowerCaseUrl.includes('.json')) return 'application/json';
        if (lowerCaseUrl.includes('.log')) return 'text/plain';
        if (lowerCaseUrl.includes('.rtf')) return 'application/rtf';

        // video
        if (lowerCaseUrl.includes('.mp4')) return 'video/mp4';
        if (lowerCaseUrl.includes('.mov')) return 'video/quicktime';
        if (lowerCaseUrl.includes('.mkv')) return 'video/x-matroska';
        if (lowerCaseUrl.includes('.avi')) return 'video/x-msvideo';
        if (lowerCaseUrl.includes('.webm')) return 'video/webm';

        // audio
        if (lowerCaseUrl.includes('.mp3')) return 'audio/mpeg';
        if (lowerCaseUrl.includes('.wav')) return 'audio/wav';
        if (lowerCaseUrl.includes('.ogg')) return 'audio/ogg';
        if (lowerCaseUrl.includes('.aac')) return 'audio/aac';
        if (lowerCaseUrl.includes('.m4a')) return 'audio/mp4';

        // images
        if (lowerCaseUrl.includes('.png')) return 'image/png';
        if (lowerCaseUrl.includes('.jpg') || lowerCaseUrl.includes('.jpeg')) return 'image/jpeg';
        if (lowerCaseUrl.includes('.gif')) return 'image/gif';
        if (lowerCaseUrl.includes('.bmp')) return 'image/bmp';
        if (lowerCaseUrl.includes('.webp')) return 'image/webp';
        if (lowerCaseUrl.includes('.svg')) return 'image/svg+xml';

        // archives
        if (lowerCaseUrl.includes('.zip')) return 'application/zip';
        if (lowerCaseUrl.includes('.rar')) return 'application/x-rar-compressed';
        if (lowerCaseUrl.includes('.7z')) return 'application/x-7z-compressed';

        return '';
    }

    /**
     * Method to download the previewed file
     */
    download() {
        if (this.isLoading || !this.currentUrl || !this.currentFileName) return;

        this.isLoading = true;

        fetch(this.currentUrl)
        .then(res => {
            if (!res.ok) {
                throw new Error('Network error');
            }
            return res.blob();
        })
        .then(blob => {
            const resolvedMimeType = this.resolveDownloadMimeType(blob.type);
            const normalizedBlob = resolvedMimeType && blob.type !== resolvedMimeType
                ? new Blob([blob], { type: resolvedMimeType })
                : blob;

            if (this.isMobileApp || this.isCCU) {
                this.appPermissionService.downloadFileInApp({
                    blob: normalizedBlob,
                    fileName: this.currentFileName,
                    mimeType: resolvedMimeType
                });
                this.isLoading = false;
            } else {
                const url = URL.createObjectURL(normalizedBlob);
                const a = document.getElementById('comments-images-download-trigger') as HTMLAnchorElement;
                if (a) {
                    a.setAttribute('href', url);
                    a.setAttribute('download', this.currentFileName);
                    a.click();
                    URL.revokeObjectURL(url);
                    this.isLoading = false;
                    a.removeAttribute('href');
                    a.removeAttribute('download');
                } else {
                    this.isLoading = false;
                }
            }
        })
        .catch(err => {
            console.error('Download failed', err);
            this.isLoading = false;
        });
    }

    // Fetch responses from signed URLs frequently return octet-stream.
    // Use known metadata/URL heuristics so native download handlers get the right type.
    private resolveDownloadMimeType(blobType: string): string {
        const normalizedBlobType = (blobType || '').toLowerCase();

        if (normalizedBlobType && normalizedBlobType !== 'application/octet-stream') {
            return normalizedBlobType;
        }

        const fallbackMimeType = (this.currentMimeType || '').toLowerCase();

        if (fallbackMimeType && fallbackMimeType !== 'application/octet-stream') {
            return fallbackMimeType;
        }

        return normalizedBlobType || 'application/octet-stream';
    }

    /**
     * Method to restore the scroll behavior of the main document
     */
    restoreScroll() {
        const body = this.document.body;
        const html = this.document.documentElement;

        body.style.overflow = this.prevBodyOverflow;
        html.style.overflow = this.prevHtmlOverflow;
    }

    /**
     * Method to close the preview popup
     */
    close(): void {
        this.closed.emit();
        this.restoreScroll();
    }
}